"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const filter_1 = __importDefault(require("lodash/filter"));
const find_1 = __importDefault(require("lodash/find"));
const get_1 = __importDefault(require("lodash/get"));
const keyBy_1 = __importDefault(require("lodash/keyBy"));
const map_1 = __importDefault(require("lodash/map"));
const size_1 = __importDefault(require("lodash/size"));
const mobx_1 = require("mobx");
const getInsuranceList_1 = __importDefault(require("../clients/insurance/getInsuranceList"));
const API_STATE_1 = __importDefault(require("../constants/app/API_STATE"));
const CDW_INSURANCE_CODE_1 = __importDefault(require("../constants/app/CDW_INSURANCE_CODE"));
const insuranceFactory_1 = __importDefault(require("../factories/insurance/insuranceFactory"));
const getInsuranceNotFound_1 = __importDefault(require("../utils/error/getInsuranceNotFound"));
const mapInitialStateToStoreState_1 = __importDefault(require("../utils/store/mapInitialStateToStoreState"));
/**
 * Insurance Domain Store
 * Stores a list of Insurance.
 */
class InsuranceStore {
    constructor(initialState, $env) {
        this.data = [];
        this.apiState = API_STATE_1.default.INITIAL;
        this.errorData = {};
        this.selectedInsurance = {}; // { code: }
        /**
         * Fetch Insurance data from backend
         * @param errorHandler { function}
         */
        this.fetch = (0, mobx_1.flow)(function* (errorHandler) {
            try {
                this.apiState = API_STATE_1.default.LOADING;
                this.data = [];
                this.errorData = {};
                // make the api call
                const insuranceResponse = yield (0, getInsuranceList_1.default)();
                const { data, total } = insuranceResponse;
                // for no data (0) response comes back with only total
                if (total < 1) {
                    // throw custom error
                    const errObj = (0, getInsuranceNotFound_1.default)();
                    this.onError(errObj, errorHandler);
                }
                else {
                    this.onSuccess(data);
                }
            }
            catch (err) {
                this.onError(err, errorHandler);
            }
        });
        /**
         * on success callback
         * @param {Array<InsuranceType>} data
         */
        this.onSuccess = (data) => {
            this.data = data;
            this.apiState = API_STATE_1.default.SUCCESS;
        };
        /**
         * on error callback
         * @param {AxiosError} err
         * @param {ErrorHandlerType} errorHandler
         */
        this.onError = (err, errorHandler) => {
            this.apiState = API_STATE_1.default.FAILED;
            if (err.response) {
                const errResponse = err.response;
                const { data, status } = errResponse;
                // stores axios error info in observable
                this.errorData = {
                    data,
                    status,
                };
                if (errorHandler) {
                    errorHandler(errResponse);
                }
            }
        };
        /**
         * Action to set selectedInsuranceId
         * @param {number} id
         */
        this.setSelectedInsurance = (insurance) => {
            this.selectedInsurance = Object.assign({}, insurance);
        };
        /**
         * Map initial state to store state
         * @param {InitialStateStoreType<InsuranceType>} initialState
         */
        this.updateInitialState = (initialState) => {
            (0, mapInitialStateToStoreState_1.default)(this, initialState);
        };
        this.$env = $env;
        this.updateInitialState(initialState);
    }
    /**
     * Returns the loading status
     * @returns {Boolean}
     */
    get isLoading() {
        return this.apiState === API_STATE_1.default.LOADING;
    }
    /**
     * Returns if API fetch was success
     * @returns {Boolean}
     */
    get isSuccess() {
        return this.apiState === API_STATE_1.default.SUCCESS;
    }
    /**
     * Returns if API fetch has failed
     * @returns {Boolean}
     */
    get hasFialed() {
        return this.apiState === API_STATE_1.default.FAILED;
    }
    /**
     * Returns true if API fetch returned no data
     * @returns {Boolean}
     */
    get hasNoData() {
        return this.isSuccess && this.count === 0;
    }
    /**
     * Returns total insurance count
     * @returns {Number}
     */
    get count() {
        return (0, size_1.default)(this.insuranceList);
    }
    /**
     * Returns current config language
     * @returns {Boolean}
     */
    get language() {
        return this.$env.config.locale;
    }
    /**
     * Returns true if insurance API has returned CDW insurance in response
     * @returns {boolean}
     */
    get hasCDWInsurance() {
        return !!(0, find_1.default)(this.insuranceList, (item) => {
            return item.code.toLowerCase() === CDW_INSURANCE_CODE_1.default;
        });
    }
    /**
     * Returns the default selected insurance code
     * This is to check the default radio button in Fleet Details Dialog
     * Basically returns the first non CDW insurance code from the response
     * @returns {string}
     */
    get defaultSelectedInsuranceCode() {
        // filter insurance list and return 0th item's code
        return (0, get_1.default)((0, filter_1.default)(this.insuranceList, (item) => {
            return item.code.toLowerCase() !== CDW_INSURANCE_CODE_1.default;
        }), '0.code', '');
    }
    /**
     * Returns the default selected insurance ID
     * This is send the default ID with fleet quote API call
     * @returns {number} 0 if not found
     */
    get defaultSelectedInsuranceId() {
        return (0, get_1.default)(this.insuranceCodeHashMap, `${this.defaultSelectedInsuranceCode}.id`, 0);
    }
    /**
     * Returns formatted insurance List
     * @returns {Array<InsuranceFactoryType>}
     */
    get insuranceList() {
        return (0, map_1.default)(this.data, (insurance) => (0, insuranceFactory_1.default)(insurance, this.language));
    }
    /**
     * Returns a hashmap
     */
    get insuranceCodeHashMap() {
        return (0, keyBy_1.default)(this.insuranceList, 'code');
    }
    /**
     * Returns selected insurance code. eg. CDW
     * @returns {string}
     */
    get selectedInsuranceCode() {
        return (0, get_1.default)(this.selectedInsurance, 'code', '');
    }
    /**
     * Returns selected insurance ID. eg. 4
     * @returns {number}
     */
    get selectedInsuranceId() {
        return (0, get_1.default)(this.insuranceCodeHashMap, `${this.selectedInsuranceCode}.id`, -1);
    }
    /**
     * Returns selected insurance name. eg. Comprehensive Insurance
     * @returns {string}
     */
    get selectedInsuranceName() {
        return (0, get_1.default)(this.insuranceCodeHashMap, `${this.selectedInsuranceCode}.name`, '');
    }
}
__decorate([
    mobx_1.observable
], InsuranceStore.prototype, "data", void 0);
__decorate([
    mobx_1.observable
], InsuranceStore.prototype, "apiState", void 0);
__decorate([
    mobx_1.observable
], InsuranceStore.prototype, "errorData", void 0);
__decorate([
    mobx_1.observable
], InsuranceStore.prototype, "selectedInsurance", void 0);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "isLoading", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "isSuccess", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "hasFialed", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "hasNoData", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "count", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "language", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "hasCDWInsurance", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "defaultSelectedInsuranceCode", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "defaultSelectedInsuranceId", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "insuranceList", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "insuranceCodeHashMap", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "selectedInsuranceCode", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "selectedInsuranceId", null);
__decorate([
    mobx_1.computed
], InsuranceStore.prototype, "selectedInsuranceName", null);
__decorate([
    mobx_1.action.bound
], InsuranceStore.prototype, "onSuccess", void 0);
__decorate([
    mobx_1.action.bound
], InsuranceStore.prototype, "onError", void 0);
__decorate([
    mobx_1.action.bound
], InsuranceStore.prototype, "setSelectedInsurance", void 0);
exports.default = InsuranceStore;
